import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useUserRegion } from '@frontend/components/agromarket/components/RegionSelector'
import { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import { Steps } from '@/components/composed'
import { ConsultingActionButton } from '@/components/composed/finansingConsulting/ConsultingActionButton'
import {
  LandingAffiliation,
  LandingApplyingOfferForm,
  LandingBenefits,
  LandingConditionsTable,
  LandingHeader,
} from '@/components/composed/landingBlocks'
import { LandingAccessControlProvider } from '@/components/composed/landingBlocks/LandingAccessControlProvider'
import { LandingFinancingProductPopups } from '@/components/composed/landingBlocks/LandingFinancingProductPopups'
import { getLocation } from '@/logic/app/reducer'
import { financingProduct } from '@/logic/metrika/constants'
import {
  getEventContextGtmFinancingOfferLendingShow,
  getGtmScrollEventPayloadFinancingOfferForm,
  pushGtmEventOnClickApplyOnFundingOfferPage,
  pushGtmEventOnShowLendingFinancingProducts,
} from '@/logic/metrika/financing/gtmHelpers'
import type { IBaseGtmEventFinancing } from '@/logic/metrika/financing/types'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { usePushScrollGtmEvent } from '@/logic/metrika/usePushScrollGtmEvent'
import type { RootState } from '@/redux/interfaces'
import type { EFinancingOfferRoute } from '@/types'
import { EFinancingOfferType } from '@/types'
import { asyncScrollIntoView } from '@/utils/helpers'
import { useWebpExtension } from '@/utils/hooks/image'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { getStaticImagePath } from '@/utils/images'
import { BENEFITS, CONDITIONS_TABLE, DETAILS, PARTNER_IMAGES, ROOT_BREADCRUMBS, STEPS } from './constants'
import { EScrollEventBlockId } from './gtmConstants'
import { messages } from './messages'
import './styles.scss'

const mapState = (state: RootState) => ({
  location: getLocation()(state),
  userGtmInfo: getUserGtmInfo()(state),
})

const InvestSber: React.FC = () => {
  const { location, userGtmInfo } = useSelector(mapState)
  const { userRegion } = useUserRegion()
  const imageExtension = useWebpExtension()

  const { isDesktop, isMobile } = useDeviceType()

  const leavingRequestClicked = () => () => {
    asyncScrollIntoView(`#${EScrollEventBlockId.financingOfferForm}`)
    pushGtmEventOnClickApplyOnFundingOfferPage(userGtmInfo, userRegion.name, FundingProduct.SBER_INVESTMENT_LOAN)
  }

  useEffect(() => {
    pushGtmEventOnShowLendingFinancingProducts(
      userGtmInfo,
      getEventContextGtmFinancingOfferLendingShow(location.pathname, financingProduct[location.pathname as EFinancingOfferRoute]),
    ) // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  usePushScrollGtmEvent<EScrollEventBlockId, IBaseGtmEventFinancing>({
    blockIds: [EScrollEventBlockId.financingOfferForm],
    gtmScrollEventPayloads: {
      [EScrollEventBlockId.financingOfferForm]: getGtmScrollEventPayloadFinancingOfferForm(
        userGtmInfo,
        userRegion.name,
        FundingProduct.SBER_INVESTMENT_LOAN,
      ),
    },
  })

  const backgroundImage = getStaticImagePath(`financingOffer/investment.${imageExtension}`)

  return (
    <LandingAccessControlProvider>
      <div className="invest-sber-page">
        <LandingHeader
          isDeviceMobile={isMobile}
          actionButtonText={messages.header.button}
          titleText={messages.header.title}
          descriptionText={messages.header.description}
          styles={{
            backgroundImage: isDesktop ? `url("${backgroundImage}")` : undefined,
          }}
          partnerImages={PARTNER_IMAGES}
          onActionClick={leavingRequestClicked()}
          details={DETAILS}
          breadcrumbCaption={messages.breadcrumb}
          rootBreadcrumbs={ROOT_BREADCRUMBS}
          additionalActions={<ConsultingActionButton />}
        />
        <LandingBenefits
          bannerTitleText={messages.benefits.title}
          bannerBackgroundImage="financingOffer/investmentBenefits"
          benefits={BENEFITS}
          onActionClick={leavingRequestClicked()}
          blockId={EScrollEventBlockId.investSberPageBenefits}
          buttonTooltipMessage={messages.financingOffer.hint.forAgrariansOnly}
        />
        <LandingAffiliation
          descriptionText={messages.affiliation.description}
          shouldShowDescription
          categories={[]}
          blockId={EScrollEventBlockId.investSberPageAffiliation}
        />
        <Steps
          blockId={EScrollEventBlockId.investSberPageStages}
          title={messages.steps.title}
          onButtonClick={leavingRequestClicked()}
          steps={STEPS}
          buttonText={messages.steps.button}
          buttonTooltipMessage={messages.financingOffer.hint.forAgrariansOnly}
        />
        <LandingConditionsTable
          blockId={EScrollEventBlockId.investSberPageConditions}
          conditionsTable={CONDITIONS_TABLE}
          tableTitleId={messages.conditions.title}
          shouldMarkFirstRow
        />
        <LandingApplyingOfferForm
          formId={EScrollEventBlockId.financingOfferForm}
          title={messages.form.title}
          offerType={EFinancingOfferType.SBER_INVESTMENT_LOAN}
          productId={FundingProduct.SBER_INVESTMENT_LOAN}
          isDarkBackground
        />
        <LandingFinancingProductPopups />
      </div>
    </LandingAccessControlProvider>
  )
}

export default InvestSber
